import BubbleCommon, {
  compareKeyMetricsEngagementTips,
  NIKKENegativeRatioTips,
  NIKKENeutralRatioTips,
  compareKeyMetricsEngagementRateTips,
  NIKKEPositiveRatioTips,
  TipsMapType,
} from '@op/components/BubbleCommon';
import HoverSortCommon from '@op/components/HoverSortCommon';
import React, { useState, useEffect } from 'react';
import { getLocale, useIntl } from 'umi';
import PercentRatioOrChart from './components/PercentRatioOrChart';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { decimalFormatPercentage } from '@op/utils/value';
import { LocaleType, PageInfoType } from '@op/types/common';
import { HeroMapType } from '@op/pages/Hero/utils';

type SortInfoType = { by: string; order: 'desc' | 'asc' };

export const tableColumnsConfig: Array<{
  key: string;
  header: string;
  valueKey: string;
  hasCharts?: boolean;
  changeSort?: boolean;
  tips?: TipsMapType;
  formatValue?: (value: number) => any;
  sortItem?: string;
}> = [
  {
    key: 'mentions',
    header: 'Mentions',
    valueKey: 'mentions',
    hasCharts: true,
    changeSort: true,
  },
  {
    key: 'mentions_ratio',
    header: 'Mentions%',
    valueKey: 'mentions_ratio',
    formatValue: decimalFormatPercentage,
    hasCharts: true,
    sortItem: 'mentions',
    changeSort: true,
    // tips: summaryMentionsRatioTips,
  },
  {
    key: 'positive_mentions_ratio',
    header: 'Positive%-2',
    valueKey: 'positive_mentions_ratio',
    formatValue: decimalFormatPercentage,
    sortItem: 'positive_mentions',
    changeSort: true,
    tips: NIKKEPositiveRatioTips,
  },
  {
    key: 'negative_mentions_ratio',
    header: 'Negative%-2',
    valueKey: 'negative_mentions_ratio',
    formatValue: decimalFormatPercentage,
    changeSort: true,
    sortItem: 'negative_mentions',
    tips: NIKKENegativeRatioTips,
  },
  {
    key: 'neutral_mentions_ratio',
    header: 'Neutral%-2',
    valueKey: 'neutral_mentions_ratio',
    formatValue: decimalFormatPercentage,
    changeSort: true,
    sortItem: 'neutral_mentions',
    tips: NIKKENeutralRatioTips,
  },
  {
    key: 'engagement',
    header: 'Engagement',
    valueKey: 'engagement',
    changeSort: true,
    tips: compareKeyMetricsEngagementTips,
  },
  {
    key: 'engagement_ratio',
    header: 'Engagement%',
    valueKey: 'engagement_ratio',
    formatValue: decimalFormatPercentage,
    changeSort: true,
    tips: compareKeyMetricsEngagementRateTips,
  },
];

export default function useNIKKETableConfig({
  timelineMap = {},
  filter,
  sort,
  setSort,
  setPageInfo,
  heroInfoMap,
}: {
  timelineMap?: any;
  filter: any;
  sort: SortInfoType;
  setSort: (value: SortInfoType) => void;
  setPageInfo: (value: PageInfoType) => void;
  heroInfoMap: HeroMapType;
}) {
  const { formatMessage } = useIntl();
  const lang: LocaleType = getLocale();
  const [columns, setColumns] = useState<Array<any>>([]);

  useEffect(() => {
    const columns: Array<any> = tableColumnsConfig.map(
      ({ key, header, valueKey, formatValue, hasCharts, changeSort, tips }) => {
        let headerText: any = (
          <HoverSortCommon
            sort={sort}
            sortOptions={[
              {
                text: formatMessage({ id: header }),
                value: valueKey,
              },
              {
                text: formatMessage({ id: `${header}: Change%` }),
                value: valueKey + '_qoq',
              },
            ]}
            sortChange={(sort: { by: string; order: 'asc' | 'desc' }) => {
              if (sort.by.includes('qoq')) {
                setPageInfo({ page: 1, page_size: 1000 });
              } else {
                setPageInfo({ page: 1, page_size: 20 });
              }
              buriedBtnPoint('Rank', sort.by);
              setSort(sort);
            }}
          >
            <BubbleCommon tipsMap={tips} showIcon={false} placement="top">
              <span>{formatMessage({ id: header })}</span>
            </BubbleCommon>
          </HoverSortCommon>
        );

        return {
          key: key,
          header: headerText,
          align: 'right',
          width: hasCharts ? 260 : 170,
          render: (record: any) => {
            const topic = record.topic.toLocaleLowerCase();

            const nameMap = heroInfoMap?.[topic] || {};
            return (
              <PercentRatioOrChart
                filter={filter}
                data={record}
                valueKey={valueKey}
                header={formatMessage({ id: header })}
                formatValue={formatValue}
                timeline={timelineMap[valueKey]?.[topic]}
                name={nameMap[lang]}
              ></PercentRatioOrChart>
            );
          },
        };
      },
    );

    setColumns(columns);
  }, [sort, formatMessage, timelineMap]);

  return {
    columns,
    sort,
    setSort,
  };
}
